import { useEffect, useMemo, useState } from "react";
import type { DocFolderNode } from "@/lib/api";

/** Which listing the page is showing. Folders carry their id; the rest are views. */
export type DocScope =
  | { kind: "folder"; folderId: string | null }
  | { kind: "recent" }
  | { kind: "starred" }
  | { kind: "mine" };

const OPEN_KEY = "docs-tree-open";

function loadOpen(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(OPEN_KEY) || "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}

interface TreeNode extends DocFolderNode {
  children: TreeNode[];
}

/** Nest the flat folder list, each level sorted by name. */
function buildTree(nodes: DocFolderNode[]): TreeNode[] {
  const byId = new Map<string, TreeNode>(nodes.map((n) => [n.id, { ...n, children: [] }]));
  const roots: TreeNode[] = [];
  for (const n of byId.values()) {
    const parent = n.parentId ? byId.get(n.parentId) : null;
    if (parent) parent.children.push(n);
    else roots.push(n);
  }
  const sortRec = (list: TreeNode[]) => {
    list.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of list) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/** Ancestor ids of a folder, so navigating anywhere reveals it in the tree. */
function ancestorsOf(nodes: DocFolderNode[], folderId: string | null): string[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const out: string[] = [];
  let cur = folderId ? byId.get(folderId)?.parentId ?? null : null;
  for (let i = 0; cur && i < 32; i++) {
    out.push(cur);
    cur = byId.get(cur)?.parentId ?? null;
  }
  return out;
}

/**
 * Persistent left pane: quick-access views on top, the full folder tree below.
 * The tree doubles as a drop target — dragging rows onto a folder moves them.
 */
export function DocsSidebar({
  tree,
  scope,
  trashCount,
  onScope,
  onOpenTrash,
  onDropOn,
}: {
  tree: DocFolderNode[];
  scope: DocScope;
  trashCount: number;
  onScope: (s: DocScope) => void;
  onOpenTrash: () => void;
  /** Rows were dropped onto a folder (null = root); the event carries the payload. */
  onDropOn: (folderId: string | null, e: React.DragEvent) => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>(loadOpen);
  const [dropTarget, setDropTarget] = useState<string | null | "none">("none");
  const roots = useMemo(() => buildTree(tree), [tree]);
  const activeFolder = scope.kind === "folder" ? scope.folderId : null;

  useEffect(() => {
    localStorage.setItem(OPEN_KEY, JSON.stringify(open));
  }, [open]);

  // Reveal the open folder: expand every ancestor on navigation.
  useEffect(() => {
    if (scope.kind !== "folder" || !scope.folderId) return;
    const anc = ancestorsOf(tree, scope.folderId);
    if (!anc.length) return;
    setOpen((o) => {
      if (anc.every((id) => o[id])) return o;
      const next = { ...o };
      for (const id of anc) next[id] = true;
      return next;
    });
  }, [scope, tree]);

  const dropProps = (folderId: string | null) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes("application/x-vernias-docs")) return;
      e.preventDefault();
      e.stopPropagation();
      setDropTarget(folderId);
    },
    onDragLeave: () => setDropTarget("none" as const),
    onDrop: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes("application/x-vernias-docs")) return;
      e.preventDefault();
      e.stopPropagation();
      setDropTarget("none");
      onDropOn(folderId, e);
    },
  });

  const isDropping = (folderId: string | null) =>
    dropTarget !== "none" && dropTarget === folderId;

  return (
    <aside className="flex w-56 shrink-0 flex-col overflow-y-auto border-r border-border px-3 py-6">
      <nav className="space-y-0.5">
        <SideLink
          icon="fa-folder-open"
          label="All files"
          active={scope.kind === "folder"}
          highlighted={isDropping(null)}
          onClick={() => onScope({ kind: "folder", folderId: null })}
          {...dropProps(null)}
        />
        <SideLink
          icon="fa-clock-rotate-left"
          label="Recent"
          active={scope.kind === "recent"}
          onClick={() => onScope({ kind: "recent" })}
        />
        <SideLink
          icon="fa-star"
          label="Starred"
          active={scope.kind === "starred"}
          onClick={() => onScope({ kind: "starred" })}
        />
        <SideLink
          icon="fa-user"
          label="My uploads"
          active={scope.kind === "mine"}
          onClick={() => onScope({ kind: "mine" })}
        />
        <SideLink
          icon="fa-trash"
          label="Trash"
          badge={trashCount || undefined}
          active={false}
          onClick={onOpenTrash}
        />
      </nav>

      <div className="my-3 border-t border-border" />

      <div className="px-2 pb-1 text-[0.6rem] font-semibold uppercase tracking-wider text-text-muted">
        Folders
      </div>
      <div className="min-h-0 flex-1">
        {roots.length === 0 ? (
          <p className="px-2 py-1 text-xs text-text-muted">No folders yet.</p>
        ) : (
          roots.map((n) => (
            <TreeRow
              key={n.id}
              node={n}
              depth={0}
              open={open}
              activeFolder={activeFolder}
              droppingId={dropTarget}
              onToggle={(id) => setOpen((o) => ({ ...o, [id]: !o[id] }))}
              onSelect={(id) => onScope({ kind: "folder", folderId: id })}
              dropProps={dropProps}
            />
          ))
        )}
      </div>
    </aside>
  );
}

function SideLink({
  icon,
  label,
  active,
  badge,
  highlighted,
  onClick,
  ...drag
}: {
  icon: string;
  label: string;
  active: boolean;
  badge?: number;
  highlighted?: boolean;
  onClick: () => void;
} & React.HTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      onClick={onClick}
      {...drag}
      className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors ${
        active
          ? "bg-accent-muted text-text-heading"
          : highlighted
            ? "bg-accent-muted text-text-heading ring-1 ring-accent"
            : "text-text-secondary hover:bg-bg-card-hover hover:text-text-heading"
      }`}
    >
      <i className={`fa-solid ${icon} w-4 text-center text-xs text-text-muted`} />
      <span className="flex-1 text-left">{label}</span>
      {badge != null && (
        <span className="rounded bg-bg-elevated px-1.5 text-[0.65rem] text-text-muted">
          {badge}
        </span>
      )}
    </button>
  );
}

function TreeRow({
  node,
  depth,
  open,
  activeFolder,
  droppingId,
  onToggle,
  onSelect,
  dropProps,
}: {
  node: TreeNode;
  depth: number;
  open: Record<string, boolean>;
  activeFolder: string | null;
  droppingId: string | null | "none";
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  dropProps: (folderId: string | null) => Record<string, unknown>;
}) {
  const isOpen = open[node.id] ?? false;
  const active = activeFolder === node.id;
  const dropping = droppingId !== "none" && droppingId === node.id;
  return (
    <>
      <div
        {...dropProps(node.id)}
        style={{ paddingLeft: `${depth * 0.75 + 0.25}rem` }}
        className={`flex items-center rounded-md pr-1 transition-colors ${
          active
            ? "bg-accent-muted"
            : dropping
              ? "bg-accent-muted ring-1 ring-accent"
              : "hover:bg-bg-card-hover"
        }`}
      >
        <button
          onClick={() => onToggle(node.id)}
          // Keep the row's click target aligned even for childless folders.
          className={`grid h-6 w-5 shrink-0 place-items-center text-text-muted ${
            node.children.length ? "hover:text-text-heading" : "invisible"
          }`}
          aria-label={isOpen ? "Collapse" : "Expand"}
        >
          <i
            className={`fa-solid fa-chevron-right text-[0.5rem] transition-transform ${
              isOpen ? "rotate-90" : ""
            }`}
          />
        </button>
        <button
          onClick={() => onSelect(node.id)}
          className={`flex min-w-0 flex-1 items-center gap-2 py-1 text-left text-[0.8rem] transition-colors ${
            active ? "text-text-heading" : "text-text-secondary hover:text-text-heading"
          }`}
        >
          <i className="fa-solid fa-folder shrink-0 text-[0.7rem] text-warning" />
          <span className="truncate">{node.name}</span>
        </button>
      </div>
      {isOpen &&
        node.children.map((c) => (
          <TreeRow
            key={c.id}
            node={c}
            depth={depth + 1}
            open={open}
            activeFolder={activeFolder}
            droppingId={droppingId}
            onToggle={onToggle}
            onSelect={onSelect}
            dropProps={dropProps}
          />
        ))}
    </>
  );
}
